/**
 * Single source of truth for the tenant template/theme presets.
 *
 * Both the admin theme picker (`ThemeAdmin.tsx`) and the runtime theme engine
 * (`ThemeContext.applyTheme`) consume these definitions, so the two can no
 * longer drift. Colours are stored as hex (what the admin swatches show) and
 * converted to HSL at apply-time for the CSS custom properties.
 */

export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  /** Hex colours — also used for the admin swatches/preview. */
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  /** Default typography for the template (can be overridden per-tenant). */
  headingFont: string;
  bodyFont: string;
  /** Layout personality — drives the `data-layout` marker and CSS treatments. */
  layoutStyle: string;
  /** Default button corner style for the template. */
  buttonStyle: string;
  /** Lightweight palette used by the admin mini-preview cards. */
  preview: { bg: string; card: string; primary: string; text: string; accent?: string };
  /**
   * Optional exact HSL triplets (`H S% L%`). When present these win over the
   * hex→HSL conversion so a designed palette lands exactly, and `background` /
   * `foreground` give the template its own light-mode page surface (applied via
   * `data-theme-surface` in tenant-premium.css so dark mode is never pinned).
   */
  hsl?: Partial<Record<'primary' | 'secondary' | 'accent' | 'background' | 'foreground', string>>;
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'classic',
    name: 'Classic Professional',
    description: 'Split hero with a framed photo, photo-topped service cards and a full four-column footer',
    primaryColor: '#66499c',
    secondaryColor: '#f5f3f7',
    accentColor: '#8b6eb8',
    headingFont: 'Palo',
    bodyFont: 'DM Sans',
    layoutStyle: 'classic',
    buttonStyle: 'rounded',
    preview: { bg: '#ffffff', card: '#f5f3f7', primary: '#66499c', text: '#1a1a2e' },
  },
  {
    id: 'modern-minimal',
    name: 'Modern Minimal',
    description: 'Huge typography, a full-width photo band, numbered service list and hairline rules',
    primaryColor: '#1a1a1a',
    secondaryColor: '#f8f8f8',
    accentColor: '#404040',
    headingFont: 'Montserrat',
    bodyFont: 'Inter',
    layoutStyle: 'modern-minimal',
    buttonStyle: 'soft',
    preview: { bg: '#ffffff', card: '#f8f8f8', primary: '#1a1a1a', text: '#1a1a1a' },
  },
  {
    id: 'warm-welcome',
    name: 'Warm & Welcoming',
    description: 'Cream surfaces, an arched photo collage, a floating pill menu and big-quote reviews',
    primaryColor: '#c4704a',
    secondaryColor: '#fdf8f4',
    accentColor: '#d4956a',
    headingFont: 'Raleway',
    bodyFont: 'Lato',
    layoutStyle: 'modern-minimal',
    buttonStyle: 'rounded',
    preview: { bg: '#fffbf7', card: '#fdf8f4', primary: '#c4704a', text: '#3d2a1f' },
  },
  {
    id: 'bold-vibrant',
    name: 'Bold & Geometric',
    description: 'Full-bleed photo hero, angled edges, a stats strip and zig-zag service rows',
    primaryColor: '#2563eb',
    secondaryColor: '#eff6ff',
    accentColor: '#3b82f6',
    headingFont: 'Montserrat',
    bodyFont: 'DM Sans',
    layoutStyle: 'bold-geometric',
    buttonStyle: 'sharp',
    preview: { bg: '#ffffff', card: '#eff6ff', primary: '#2563eb', text: '#1e293b' },
  },
  {
    id: 'elegant-serif',
    name: 'Elegant Serif',
    description: 'Editorial serif look with a centred masthead, a service menu with dotted leaders and thin rules',
    primaryColor: '#4a4a4a',
    secondaryColor: '#fafafa',
    accentColor: '#6a6a6a',
    headingFont: 'Playfair Display',
    bodyFont: 'Lato',
    layoutStyle: 'elegant-serif',
    buttonStyle: 'rounded',
    preview: { bg: '#fffef8', card: '#fafafa', primary: '#4a4a4a', text: '#2a2a2a' },
  },
  {
    id: 'fresh-natural',
    name: 'Fresh & Natural',
    description: 'Arch-shaped photo, leafy green accents, offset service cards and rounded pills',
    primaryColor: '#16a34a',
    secondaryColor: '#f0fdf4',
    accentColor: '#22c55e',
    headingFont: 'Raleway',
    bodyFont: 'Open Sans',
    layoutStyle: 'playful-rounded',
    buttonStyle: 'pill',
    preview: { bg: '#fafff7', card: '#f0fdf4', primary: '#16a34a', text: '#14532d' },
  },
  {
    id: 'modern',
    name: 'Modern',
    description: 'Dark ink hero with a floating photo card, bento-grid services and a teal accent',
    primaryColor: '#392c6d',
    secondaryColor: '#f2f1f9',
    accentColor: '#39ac9d',
    headingFont: 'DM Sans',
    bodyFont: 'DM Sans',
    layoutStyle: 'modern',
    buttonStyle: 'pill',
    preview: { bg: '#ffffff', card: '#f2f1f9', primary: '#392c6d', text: '#191730', accent: '#39ac9d' },
    hsl: {
      primary: '252 42% 30%',
      secondary: '250 40% 96%',
      accent: '172 50% 45%',
      background: '0 0% 100%',
      foreground: '245 35% 14%',
    },
  },
];

export function getPreset(id: string | null | undefined): ThemePreset {
  return THEME_PRESETS.find((t) => t.id === id) ?? THEME_PRESETS[0];
}

/** Corner radius for each button style, shared by the runtime and the admin preview. */
export const BUTTON_RADIUS: Record<string, string> = {
  rounded: '0.75rem',
  pill: '9999px',
  sharp: '0',
  soft: '0.5rem',
};

/**
 * Card/control radius by layout personality. This stays separate from button
 * shape so a tenant can choose pill buttons without turning dashboard cards
 * and form panels into giant ovals.
 */
export const LAYOUT_RADIUS: Record<string, string> = {
  classic: '0.75rem',
  'modern-minimal': '0.875rem',
  'bold-geometric': '0.375rem',
  'elegant-serif': '0.875rem',
  'playful-rounded': '1rem',
  modern: '1rem',
};

/**
 * Full font-family stacks for every offered font, each with a real fallback
 * chain. The leading families are loaded via Google Fonts in `index.html`
 * ("Palo" maps to the locally-hosted `Polished Font`), so selecting a font
 * now renders that font rather than a silent generic fallback.
 */
export const FONT_STACKS: Record<string, string> = {
  Palo: "'Polished Font', 'DM Sans', system-ui, sans-serif",
  'Playfair Display': "'Playfair Display', Georgia, 'Times New Roman', serif",
  Montserrat: "'Montserrat', 'DM Sans', system-ui, sans-serif",
  Raleway: "'Raleway', 'DM Sans', system-ui, sans-serif",
  'DM Sans': "'DM Sans', system-ui, sans-serif",
  Inter: "'Inter', 'DM Sans', system-ui, sans-serif",
  'Open Sans': "'Open Sans', 'DM Sans', system-ui, sans-serif",
  Lato: "'Lato', 'DM Sans', system-ui, sans-serif",
};

export function fontStack(name: string | null | undefined): string {
  if (!name) return FONT_STACKS['DM Sans'];
  return FONT_STACKS[name] ?? `'${name}', 'DM Sans', system-ui, sans-serif`;
}

/** Convert a `#rrggbb` hex colour to an `H S% L%` triple for CSS custom properties. */
export function hexToHsl(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '261 36% 45%';

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export const THEME_PREVIEW_FRAME_PATH = '/admin/theme-preview-frame';

/**
 * Parse the theme forced on the admin preview iframe
 * (`/admin/theme-preview-frame?preset=…&hf=…&bf=…&bs=…&pc=…&bt=…`).
 * Returns null anywhere else, so the public site can never be forced.
 */
export function parseForcedTheme(
  pathname: string,
  search: string,
): { theme_name: string; primary_color: string | null; button_text_color: string | null; button_style: string; heading_font: string; body_font: string; layout_style: string } | null {
  if (!pathname.startsWith(THEME_PREVIEW_FRAME_PATH)) return null;
  const q = new URLSearchParams(search);
  const preset = getPreset(q.get('preset'));
  const hex = (v: string | null) => (v && /^#[0-9a-f]{6}$/i.test(v) ? v : null);
  const font = (v: string | null, fallback: string) => (v && v in FONT_STACKS ? v : fallback);
  const style = q.get('bs');
  return {
    theme_name: preset.id,
    primary_color: hex(q.get('pc')),
    button_text_color: hex(q.get('bt')),
    button_style: style && style in BUTTON_RADIUS ? style : preset.buttonStyle,
    heading_font: font(q.get('hf'), preset.headingFont),
    body_font: font(q.get('bf'), preset.bodyFont),
    layout_style: preset.layoutStyle,
  };
}
